'use client';
import { first } from 'lodash';
import { createContext, useContext, useMemo } from 'react';

import { isDeveloperMode } from 'config/env';
import { GqlTeacherProfile, IGrantedPermission, Permissions } from 'model/profiles';
import { UserTeacherPermissionsGql } from 'model/User';
import { useTeacherfirstInit } from 'network/graphql/Initialization/teacherfirst';
import useLocalForageState from 'utils/hooks/useLocalForageState';

export interface ITeacherContext {
  selectedTeacher: GqlTeacherProfile & { permissions: IGrantedPermission[] };
  setTeacher: (teacherID: string) => Promise<void>;
  teacherProfiles: GqlTeacherProfile[];
  refetch?: ReturnType<typeof useTeacherfirstInit>['refetch'];
  hasPermission: (permission: Permissions) => boolean;
}

export const TeacherContext = createContext<ITeacherContext>({} as ITeacherContext);

const useTeacherContext = () => useContext(TeacherContext);

export const useUserToTeacherContext = ({
  user,
  refetch,
}: {
  user?: UserTeacherPermissionsGql;
  refetch?: ReturnType<typeof useTeacherfirstInit>['refetch'];
}): ITeacherContext | null => {
  const [selectedTeacherId, setSelectedTeacherIdAsync] = useLocalForageState('selected_teacher_id');
  const selectedTeacher = useMemo(() => {
    if (!user) {
      return undefined;
    }
    const selectedTeacherProfile =
      user?.teacherProfiles?.find((t) => t.id === selectedTeacherId) || first(user.teacherProfiles);
    return selectedTeacherProfile;
  }, [user, selectedTeacherId]);

  if (!user || !selectedTeacher) {
    return null;
  }

  const bypassPermissions = isDeveloperMode();

  return {
    selectedTeacher,
    teacherProfiles: user?.teacherProfiles || [],
    setTeacher: setSelectedTeacherIdAsync,
    refetch,
    hasPermission: (permission: Permissions) =>
      bypassPermissions || selectedTeacher.permissions.some((p) => p.permission === permission),
  };
};

export default useTeacherContext;
